import { z } from 'zod';
import { mediaIdSchema, mediaTypeSchema, type MediaType } from './content';

export const PARTY_MAX_MEMBERS = 8;
export const PARTY_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;

export const partyCodeSchema = z
  .string()
  .transform((s) => s.trim().toUpperCase())
  .pipe(z.string().regex(PARTY_CODE_PATTERN, 'Invalid invite code'));

export const createPartySchema = z.object({
  type: mediaTypeSchema,
  id: mediaIdSchema,
  season: z.number().int().min(0).max(1000).nullable().default(null),
  episode: z.number().int().min(0).max(10000).nullable().default(null),
});
export type CreatePartyInput = z.input<typeof createPartySchema>;

/** A new party starts empty, in the menus. Apps before 0.6.13 still send the title to watch. */
export const newPartySchema = z.union([
  createPartySchema,
  z
    .object({})
    .strict()
    .transform(() => null),
]);

export const PARTY_REACTIONS = ['😂', '😮', '😍', '👏', '😢', '🔥', '😱', '❤️'] as const;
export const PARTY_CHAT_MAX = 300;
export const PARTY_POLL_MAX = 20;
export const READY_CHECK_MS = 30_000;

const memberIdSchema = z.string().min(1).max(40);

export const partyActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('start') }),
  z.object({
    type: z.literal('chat'),
    text: z
      .string()
      .transform((s) => s.replace(/\s+/g, ' ').trim())
      .pipe(z.string().min(1, 'Type a message').max(PARTY_CHAT_MAX)),
  }),
  z.object({ type: z.literal('react'), emoji: z.enum(PARTY_REACTIONS) }),
  z.object({ type: z.literal('readyCheck') }),
  z.object({ type: z.literal('readyAnswer'), ready: z.boolean() }),
  z.object({
    type: z.literal('settings'),
    hostOnlyControls: z.boolean().optional(),
    locked: z.boolean().optional(),
  }),
  z.object({ type: z.literal('kick'), memberId: memberIdSchema }),
  z.object({ type: z.literal('makeHost'), memberId: memberIdSchema }),
  z.object({ type: z.literal('vote'), optionId: memberIdSchema.nullable() }),
  z.object({ type: z.literal('unnominate'), optionId: memberIdSchema }),
  z.object({ type: z.literal('playNext'), optionId: memberIdSchema }),
  z.object({ type: z.literal('startNow'), optionId: memberIdSchema }),
  z.object({
    type: z.literal('reorder'),
    optionId: memberIdSchema,
    index: z.number().int().min(0).max(100),
  }),
  z.object({ type: z.literal('countdown'), paused: z.boolean() }),
  z.object({ type: z.literal('finished') }),
  z.object({ type: z.literal('stop') }),
  z.object({ type: z.literal('sitOut'), out: z.boolean() }),
  z.object({
    type: z.literal('looking'),
    place: z.string().trim().max(40),
    media: z
      .object({
        type: mediaTypeSchema,
        id: mediaIdSchema,
        title: z.string().trim().min(1).max(200),
      })
      .nullable(),
  }),
  z.object({ type: z.literal('play'), position: z.number().min(0).max(1e6) }),
  z.object({ type: z.literal('pause'), position: z.number().min(0).max(1e6) }),
  z.object({ type: z.literal('seek'), position: z.number().min(0).max(1e6) }),
  z.object({ type: z.literal('status'), ready: z.boolean(), buffering: z.boolean() }),
  z.object({
    type: z.literal('stream'),
    fingerprint: z.string().max(600),
    title: z.string().max(300),
  }),
  z.object({ type: z.literal('end') }),
  z.object({ type: z.literal('leave') }),
]);
export type PartyAction = z.infer<typeof partyActionSchema>;

export type PartyStatus = 'lobby' | 'starting' | 'playing' | 'paused' | 'ended';

export interface PartyMedia {
  type: MediaType;
  id: string;
  season: number | null;
  episode: number | null;
  title: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  year: number | null;
}

export interface PartyMember {
  id: string;
  name: string;
  avatar: string;
  avatarUrl: string | null;
  isHost: boolean;
  connected: boolean;
  ready: boolean;
  buffering: boolean;
  /** In the party but staying in the menus while the film plays. */
  sittingOut: boolean;
  /** What they are looking at in the menus. */
  activity: PartyActivityNow | null;
  /** Tea+ Premium (in testing): a gold plus next to the name. */
  premium?: boolean;
}

/**
 * How big a party can get, set by the owner's plan when it starts (Tea+ Premium, in testing). It
 * stays the same for the whole party, even if someone else becomes the owner.
 */
export interface PartyPlan {
  maxMembers: number;
  maxQueue: number;
  /** Votes decide what plays next. */
  upNextVoting: boolean;
  /** The owner's Premium made it bigger. */
  premium: boolean;
  /** Plans apply, so a full Free party can say Premium ones are bigger. */
  plans: boolean;
}

export interface PartyActivityNow {
  place: string;
  media: { type: MediaType; id: string; title: string } | null;
}

export interface PartyPause {
  by: string;
  reason: 'paused' | 'buffering';
}

export interface PartySettings {
  hostOnlyControls: boolean;
  locked: boolean;
}

export interface PartyReadyCheck {
  id: string;
  by: string;
  endsAt: number;
  answers: Record<string, boolean>;
  done: boolean;
}

export interface PartyPollOption {
  id: string;
  media: PartyMedia;
  by: string;
  byMemberId: string;
  votes: string[];
  /** Whether each member's own addons and provider found a stream, by member id. */
  playable: Record<string, boolean>;
}

/** "Up next in 15s": paused when pausedLeft is set. What plays is the first title in the queue. */
export interface PartyCountdown {
  endsAt: number;
  pausedLeft: number | null;
}

export interface PartyState {
  code: string;
  /** 'lobby' means everyone is in the menus, whether or not a title has been picked. */
  status: PartyStatus;
  /** Always set, for apps before 0.6.13: a stand-in called "Party" unless hasTitle is true. */
  media: PartyMedia;
  hasTitle: boolean;
  /** Goes up each time a title starts. */
  round: number;
  lastPlayed: PartyMedia | null;
  countdown: PartyCountdown | null;
  members: PartyMember[];
  you: string | null;
  position: number;
  anchor: number;
  serverNow: number;
  pause: PartyPause | null;
  hostStream: { fingerprint: string; title: string } | null;
  endedReason: string | null;
  settings: PartySettings;
  readyCheck: PartyReadyCheck | null;
  poll: PartyPollOption[];
  /** Missing from servers before Tea+ Premium. */
  plan?: PartyPlan;
  /** The queued title that plays next: the most votes with Up Next voting, else the first. */
  nextId?: string | null;
  version: number;
}

export interface PartyChatMessage {
  id: string;
  memberId: string | null;
  name: string;
  text: string;
  at: number;
  kind: 'chat' | 'system';
}

export interface PartyReaction {
  id: string;
  memberId: string;
  name: string;
  emoji: (typeof PARTY_REACTIONS)[number];
  at: number;
}

export type PartyEvent =
  | { type: 'state'; state: PartyState }
  | { type: 'history'; messages: PartyChatMessage[] }
  | { type: 'chat'; message: PartyChatMessage }
  | { type: 'reaction'; reaction: PartyReaction };

/** Adding a title to the queue; startNow (owner only) plays it straight away instead. */
export const nominateSchema = createPartySchema.extend({
  startNow: z.boolean().default(false),
});

export function partyPosition(s: Pick<PartyState, 'status' | 'position' | 'anchor'>, now: number) {
  return s.status === 'playing' ? s.position + Math.max(0, now - s.anchor) / 1000 : s.position;
}

export function partyMediaLabel(m: Pick<PartyMedia, 'type' | 'title' | 'season' | 'episode'>) {
  return m.type === 'series' && m.season != null
    ? `${m.title} S${m.season} E${m.episode}`
    : m.title;
}

/** "3 of 4 can play this", once every member's addons have answered. */
/** The queued title that plays next (older servers: always the first). */
export function partyNext(state: Pick<PartyState, 'poll' | 'nextId'>) {
  return state.poll.find((o) => o.id === state.nextId) ?? state.poll[0] ?? null;
}

/**
 * Whether the queue shows votes. Free parties under Tea+ Premium (in testing) don't, as votes
 * there don't change anything; before Premium they always showed.
 */
export function partyVoting(state: Pick<PartyState, 'plan'>) {
  return !state.plan?.plans || state.plan.upNextVoting;
}

export function partyPlayableText(o: PartyPollOption, members: PartyMember[]) {
  if (members.some((m) => !(m.id in o.playable))) return 'Checking who can play this...';
  const can = members.filter((m) => o.playable[m.id]).length;
  return `${can} of ${members.length} can play this`;
}
